import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager"

const INIT_STATE = {
    enrichedPoints: null,
    loading: false,
    error: null
};

const route = "src/modules/realtimeMaps/main/redux/slice";

/**
 * Thunk to get the speed limit of an array of points
 * using the GraphQL query:
 * getMapSpeedLimits(paths: [[MapCoordinateInput!]!]!): [AWSJSON]
 */
export const getMapSpeedLimitsForPaths = createAsyncThunk(
  'mapSpeedLimits/getMapSpeedLimitsForPaths',
  async ({ path, callback }, { rejectWithValue }) => {
    try {
        // Copy the original array to avoid modifying it
        let totalRoute = [...path];

        // Split the array into chunks of a maximum of 100 points
        const chunkedPaths = [];
        while (totalRoute.length > 0) {
            const chunk = totalRoute.splice(0, 100);
            chunkedPaths.push(chunk);
        }
    
        // Helper function to format a chunk to the format expected by the query:
        // an array of objects { lat, lng }
        const formatChunk = (chunk) =>
            chunk.map(pt => ({
                lat: pt.lat(),
                lng: pt.lng()
            }));

        // Format all chunks
        const formattedChunks = chunkedPaths.map(chunk => formatChunk(chunk));

        // Perform queries in parallel for each chunk.
        // The query expects an array of arrays: paths: [[MapCoordinateInput!]!]!
        const results = await Promise.all(
            formattedChunks.map(chunk =>
                clientQuery(
                    `query($paths: [[MapCoordinateInput!]!]!) {
                        getMapSpeedLimits(paths: $paths)
                    }`,
                    { paths: [chunk] },
                    endpoints.GRAPHQL_GENERAL_V2
                ).then(response => {
                    // It is assumed that the response is located in response.getMapSpeedLimits[0]
                    // and that it is a JSON string (parse it to get an object with snappedPoints and speedLimits)
                    return response?.getMapSpeedLimits[0] && JSON.parse(response.getMapSpeedLimits[0]);
                })
            )
        );
    
        // Flatten the results into a single array
        const mergedSpeedLimits = results.reduce((acc, curr) => [...acc, ...curr.speedLimits], []);

        // Create the enriched final array, associating each point with its speed data.
        // It is assumed that the order of the data matches the order of the original array.
        const enrichedPoints = path.map((pt, index) => ({
            lat: pt.lat(),
            lng: pt.lng(),
            speedLimit: mergedSpeedLimits[index]?.speedLimit ?? 0,
            placeId: mergedSpeedLimits[index]?.placeId ?? '',
            units: mergedSpeedLimits[index]?.units ?? ''
        }));
        
        if (callback && typeof callback === 'function') {
            callback(enrichedPoints, mergedSpeedLimits);
        }
        return enrichedPoints;

    } catch (error) {
        ExceptionManager(error, route, 'getMapSpeedLimitsForPaths');
        return rejectWithValue(error);
    }
  }
);

export const RealtimeMapsSliceRedux = createSlice({
    name: route,
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getMapSpeedLimitsForPaths.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(getMapSpeedLimitsForPaths.fulfilled, (state, action) => {
            state.enrichedPoints = action.payload;
            state.loading = false;
        });
        builder.addCase(getMapSpeedLimitsForPaths.rejected, (state, action) => {
            state.enrichedPoints = null;
            state.error = action.payload;
            state.loading = false;
        });
    }
});

const { actions, reducer } = RealtimeMapsSliceRedux;
export const { reset } = actions;
export default reducer;